<script setup lang="ts">
import {Ref, ref} from "vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {QUploader} from "quasar";
import AttachmentIcon from "@/assets/widgetIcons/AttachmentIcon.vue";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import SignatureIcon from "@/assets/widgetIcons/SignatureIcon.vue";
import ScanIcon from "@/assets/widgetIcons/ScanIcon.vue";
import ImagePreviewer from "@/components/dialogs/ImagePreviewer.vue";
import FileUploaderWidgetUtil from "@/utils/editView/widgets/FileUploaderWidgetUtil.ts";

let props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
let fieldUtil = new FileUploaderWidgetUtil(props.presenter, props.fieldMetaData, fieldId, undefined, props.field as namaTypes.QuestionField);
const url = fieldUtil.computedUrl();
const uploader: Ref<QUploader | undefined> = ref();
const imageVisible = ref(false)
let mouseIn = false;
let loadingStore = useLoadingStore();

async function startUpload(e: MouseEvent) {
  if (uploader.value) {
    uploader.value.pickFiles(e);
  }
}

async function onFilesDropped(e: DragEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  const files = e.dataTransfer?.files;
  if (!files || files.length === 0 || !uploader.value)
    return;
  uploader.value.reset();
  uploader.value.addFiles(files);
}

function resetUploaderQueue() {
  loadingStore.stopLoading();
  uploader.value?.reset();
}

async function onScanClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  if (e.altKey || e.ctrlKey) {
    await startUpload(e);
  } else {
    await FileUploaderWidgetUtil.startScan(fieldUtil);
  }
}

async function onAttachmentClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  if (e.altKey || e.ctrlKey)
    await FileUploaderWidgetUtil.startScan(fieldUtil)
  else
    await startUpload(e);
}

function mouseEnter() {
  mouseIn = true;
  if (imageVisible.value)
    return;
  setTimeout(() => {
    if (!mouseIn)
      return;
    imageVisible.value = true;
    return;
  }, 1000);
}


function mouseLeave() {
  mouseIn = false;
}
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)" :isSpanned="isSpanned"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget>
      <div
        class="tw-w-full"
        @dragenter.capture.prevent.stop
        @dragover.capture.prevent.stop
        @dragleave.capture.stop
        @drop.capture.prevent.stop="onFilesDropped">
      <q-uploader
        ref="uploader"
        :id="field.id"
        auto-upload flat dense hide-bottom-space no-thumbnails
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
        @rejected="fieldUtil.onRejected"
        :url="FileUploaderWidgetUtil.getUrl"
        @start="loadingStore.startLoading()"
        @finish="resetUploaderQueue"
        @uploaded="fieldUtil.onPartialUserInput"
        :multiple="false"
        :class="{'nama-uploader-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-uploader':!fieldUtil.isFieldDisabled(forceEnableField)}">
        <template #header/>
        <template #list>
          <div
            class="tw-flex tw-items-center tw-justify-between tw-w-full tw-pr-[2px] tw-pl-[6px] compact:tw-pr-0 ultra:tw-pr-0 compact:tw-pl-[3px] ultra:tw-pl-[1px] tw-py-0 tw-max-h-[40px] compact:tw-max-h-7 ultra:tw-max-h-5">
            <q-avatar v-if="fieldUtil.eSignature" square @click="FileUploaderWidgetUtil.onSignatureClick(fieldUtil, forceEnableField)"
                      title="Open Signature Popup"
                      class="nama-avatar-hover tw-cursor-pointer">
              <SignatureIcon class="tw-w-[18px] tw-h-[18px] nama-widget-icon-hover nama-widget-icon"/>
            </q-avatar>
            <q-avatar v-else-if="fieldUtil.scanByDefault" square @click="onScanClick" class="nama-avatar-hover tw-cursor-pointer">
              <q-uploader-add-trigger v-if="fieldUtil.isAttachmentExist() && !fieldUtil.isFieldDisabled(forceEnableField)"
                                      title="Scan Attachment (Alt Click for Upload)"/>
              <ScanIcon class="tw-w-[18px] tw-h-[18px] nama-widget-icon-hover nama-widget-icon"/>
            </q-avatar>
            <q-avatar v-else-if="!fieldUtil.eSignature && !fieldUtil.scanByDefault" square @click="onAttachmentClick"
                      class="nama-avatar-hover tw-cursor-pointer">
              <q-uploader-add-trigger v-if="fieldUtil.isAttachmentExist()  && !fieldUtil.isFieldDisabled(forceEnableField)"
                                      title="Upload Attachment (Alt Click for Scanner)"/>
              <AttachmentIcon class="tw-w-[18px] tw-h-[18px] nama-widget-icon-hover nama-widget-icon"/>
            </q-avatar>
            <q-uploader-add-trigger
              v-if="!fieldUtil.isAttachmentExist() && !fieldUtil.eSignature && !fieldUtil.scanByDefault && !fieldUtil.isFieldDisabled(forceEnableField)"/>
            <div v-if="fieldUtil.isAttachmentExist()" class="tw-flex tw-items-center tw-gap-2">
              <a :href="url">
                <q-chip
                  removable
                  @remove="fieldUtil.removeFile(forceEnableField)"
                  @mouseenter="mouseEnter"
                  @mouseleave="mouseLeave"
                  clickable
                  square
                  class="nama-chip"
                  :label="fieldUtil.getFileName()"
                  :title="fieldUtil.getFileName()">
                </q-chip>
              </a>
            </div>
          </div>
          <ImagePreviewer v-if="fieldUtil.isAttachmentExist()" v-model="imageVisible"
                          :src="url" :fileName="fieldUtil.getFileName()"/>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-uploader>
      </div>
    </template>
  </WidgetContainer>
</template>
